<template>
  <div class="coin-exchange-page">
    <div class="page-content">
      <!-- Page Header -->
      <div class="page-header">
        <button class="back-btn" @click="goBack">
          <feather-icon icon="ChevronLeftIcon" size="20" />
        </button>
        <h1 class="page-title">แลกเครดิต</h1>
        <button class="info-btn" @click="showInfoModal = true">
          <feather-icon icon="InfoIcon" size="20" />
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-container">
        <b-spinner variant="warning" />
        <p class="loading-text">กำลังโหลด...</p>
      </div>

      <!-- Main Content -->
      <div v-else class="main-content">
        <!-- Exchange Card -->
        <div class="exchange-card">
          <div class="exchange-row">
            <span class="exchange-label">เหรียญ ที่มี</span>
            <span class="exchange-value">
              <span class="coin-icon">🪙</span>
              {{ formatNumber(coinBalance) }}
            </span>
          </div>

          <!-- Exchange Amount Input -->
          <div class="exchange-input-section">
            <label class="exchange-input-label">จำนวนที่ต้องการแลก</label>
            <div class="exchange-input-wrapper">
              <span class="input-icon">🪙</span>
              <input
                v-model.number="exchangeAmount"
                type="number"
                class="exchange-input"
                :min="100"
                :max="maxExchangeAmount"
                placeholder="100-300"
              />
              <button
                class="max-btn"
                @click="exchangeAmount = maxExchangeAmount"
              >
                MAX
              </button>
            </div>
            <div class="input-hint">
              ขั้นต่ำ 100 | สูงสุด {{ maxExchangeAmount }} เหรียญ
            </div>
          </div>

          <div class="exchange-arrow">↓</div>
          <div class="exchange-row">
            <span class="exchange-label">เครดิตที่จะได้รับ</span>
            <span class="exchange-value credit-value">
              {{ formatNumber(exchangeAmount) }} ฿
            </span>
          </div>
          <p class="exchange-rate">อัตราแลก: 1 เหรียญ = 1 เครดิต</p>

          <!-- Warning conditions -->
          <div class="exchange-warnings">
            <p class="warning-item">
              ⚠️ ขั้นต่ำ: 100 เหรียญ | สูงสุด: 300 เหรียญ ต่อครั้ง
            </p>
            <p class="warning-item">⚠️ เครดิตต้องมีน้อยกว่า 10 บาท</p>
          </div>

          <button
            class="confirm-btn"
            :disabled="isExchanging || !canExchange"
            @click="exchangeCoins"
          >
            {{ isExchanging ? 'กำลังแลก...' : 'ยืนยันแลก' }}
          </button>
        </div>

        <!-- Exchange History Section -->
        <div class="history-section">
          <div class="section-header">
            <h3 class="section-title">📜 ประวัติการแลกเครดิต</h3>
          </div>

          <div class="history-list">
            <div v-if="exchangeHistory.length === 0" class="empty-state">
              <span class="empty-icon">🎲</span>
              <p class="empty-text">ยังไม่มีประวัติการแลกเครดิต</p>
            </div>

            <div v-else class="history-items">
              <div
                v-for="(item, index) in exchangeHistory"
                :key="item.id"
                class="history-item"
              >
                <div class="history-index">
                  {{ index + 1 }}
                </div>
                <div class="history-info">
                  <div class="history-prize">
                    <span class="prize-icon">🪙</span>
                    <span class="prize-value">
                      {{ formatNumber(item.coins_exchanged) }}
                    </span>
                    <span class="prize-arrow">→</span>
                    <span class="prize-credit">
                      {{ formatNumber(item.credit_received) }} ฿
                    </span>
                  </div>
                  <div class="history-date">
                    {{ item.created_at }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Info Modal -->
      <div
        v-if="showInfoModal"
        class="modal-overlay"
        @click.self="showInfoModal = false"
      >
        <div class="info-modal">
          <div class="modal-header">
            <h3 class="modal-title">📋 เงื่อนไขการแลกเครดิต</h3>
            <button class="modal-close" @click="showInfoModal = false">
              ×
            </button>
          </div>
          <div class="modal-body">
            <ul class="terms-list">
              <li>ขั้นต่ำในการแลก: <strong>100 เหรียญ</strong></li>
              <li>สูงสุดในการแลก: <strong>300 เหรียญ ต่อครั้ง</strong></li>
              <li>
                ยอดเครดิตต้องมีน้อยกว่า <strong>10 บาท</strong> ถึงจะแลกได้
              </li>
              <li>อัตราแลกเปลี่ยน: <strong>1 เหรียญ = 1 เครดิต</strong></li>
              <li>เครดิตที่แลกจะเข้าบัญชีทันที</li>
              <li>ไม่สามารถแลกเหรียญกลับเป็นเพชรได้</li>
            </ul>
          </div>
          <div class="modal-actions">
            <button class="confirm-btn" @click="showInfoModal = false">
              เข้าใจแล้ว
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { BSpinner } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'CoinExchangePage',
  components: {
    BSpinner
  },
  data() {
    return {
      isLoading: true,
      isExchanging: false,
      showInfoModal: false,
      exchangeAmount: 100, // User-selected amount to exchange
      coinBalance: 0,
      exchangeHistory: []
    }
  },
  computed: {
    ...mapGetters('member', ['balance']),

    maxExchangeAmount() {
      // Maximum 300 coins per exchange, or user's balance if less
      return Math.min(300, this.coinBalance)
    },

    canExchange() {
      // Minimum 100 coins required, max 300, credit balance must be less than 10
      return (
        this.exchangeAmount >= 100 &&
        this.exchangeAmount <= 300 &&
        this.exchangeAmount <= this.coinBalance &&
        this.balance < 10
      )
    }
  },
  methods: {
    ...mapActions('member', ['fetchCredit']),

    goBack() {
      this.$router.go(-1)
    },

    formatNumber(num) {
      if (!num && num !== 0) return '0'
      return new Intl.NumberFormat('th-TH').format(num)
    },

    async fetchCoinBalance() {
      try {
        const response = await this.$http.get('/member/coin/balance')
        if (response.data && response.data.success && response.data.data) {
          this.coinBalance = response.data.data.current_balance || 0
        }
      } catch (error) {
        console.error('[CoinExchange] Error fetching coin balance:', error)
        this.showErrorToast('ไม่สามารถโหลดข้อมูลได้')
      }
    },

    async fetchExchangeHistory() {
      try {
        const response = await this.$http.get(
          '/member/coin/exchange/history?limit=10'
        )
        if (response.data && response.data.success && response.data.data) {
          this.exchangeHistory = response.data.data.history || []
        } else {
          this.exchangeHistory = []
        }
      } catch (error) {
        console.error('[CoinExchange] Error fetching exchange history:', error)
        this.exchangeHistory = []
      }
    },

    async exchangeCoins() {
      if (!this.canExchange) return

      this.isExchanging = true
      const coinsToExchange = this.exchangeAmount // Save before API call

      try {
        const response = await this.$http.post('/member/coin/exchange', {
          coins: coinsToExchange
        })

        const { data } = response.data

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'แลกสำเร็จ!',
            text: `ได้รับ ${this.formatNumber(coinsToExchange)} เครดิต`,
            icon: 'CheckCircleIcon',
            variant: 'success'
          }
        })

        this.coinBalance = data.new_coin_balance
        this.exchangeAmount = 100 // Reset to default

        // Refresh history list and balance
        await Promise.all([this.fetchExchangeHistory(), this.fetchCredit()])
        if (this.$eventBus) {
          this.$eventBus.$emit('refreshBalance')
        }
      } catch (error) {
        console.error('Error exchanging coins:', error)
        const errorMessage =
          error.response?.data?.message || 'ไม่สามารถแลกได้ กรุณาลองใหม่'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาด',
            text: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.isExchanging = false
      }
    },

    showErrorToast(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'เกิดข้อผิดพลาด',
          text: message,
          icon: 'AlertCircleIcon',
          variant: 'danger'
        }
      })
    }
  },

  async beforeMount() {
    await Promise.all([
      this.fetchCoinBalance(),
      this.fetchExchangeHistory(),
      this.fetchCredit()
    ])
    // Default to max allowed (or minimum 100) once balance is known
    this.exchangeAmount = Math.max(100, Math.min(300, this.coinBalance))
    this.isLoading = false
  }
}
</script>

<style lang="scss" scoped>
.coin-exchange-page {
  width: 100%;
  min-height: 100vh;
  padding: 8px;
  padding-bottom: 80px;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (min-width: 768px) {
    padding: 12px;
    padding-bottom: 100px;
  }
}

.page-content {
  max-width: 400px;
  margin: 0 auto;
  width: 100%;
}

// Page Header
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 8px 0;
}

.back-btn,
.info-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }
}

.page-title {
  font-size: 20px;
  font-weight: bold;
  color: #ffcc00;
  margin: 0;
  text-align: center;
  flex: 1;

  @media (min-width: 768px) {
    font-size: 24px;
  }
}

// Loading State
.loading-container {
  text-align: center;
  padding: 54px 18px;
}

.loading-text {
  color: rgba(255, 255, 255, 0.7);
  margin-top: 14px;
  font-size: 14px;
}

// Main content
.main-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// Exchange Card
.exchange-card {
  background: linear-gradient(
    135deg,
    rgba(40, 167, 69, 0.08),
    rgba(32, 201, 151, 0.04)
  );
  border: 1px solid rgba(40, 167, 69, 0.25);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}

.exchange-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  margin-bottom: 6px;
}

.exchange-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.exchange-value {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 700;
  color: #ffd700;

  .coin-icon {
    font-size: 14px;
  }

  &.credit-value {
    color: #28a745;
  }
}

// Exchange Input Section
.exchange-input-section {
  margin: 12px 0;
  text-align: left;
}

.exchange-input-label {
  display: block;
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  margin-bottom: 6px;
}

.exchange-input-wrapper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 4px 8px;
  gap: 8px;

  &:focus-within {
    border-color: rgba(255, 204, 0, 0.5);
  }
}

.input-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.exchange-input {
  flex: 1;
  background: transparent;
  border: none;
  color: #ffd700;
  font-size: 16px;
  font-weight: 700;
  padding: 8px 0;
  outline: none;
  min-width: 0;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
}

.max-btn {
  background: linear-gradient(135deg, #ffcc00, #ff9900);
  border: none;
  border-radius: 4px;
  color: #1a1a2e;
  font-size: 10px;
  font-weight: 700;
  padding: 6px 10px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.input-hint {
  color: rgba(255, 255, 255, 0.4);
  font-size: 10px;
  margin-top: 4px;
}

.exchange-arrow {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.4);
  margin: 6px 0;
}

.exchange-rate {
  color: rgba(255, 255, 255, 0.5);
  font-size: 10px;
  margin: 10px 0 0 0;
}

.exchange-warnings {
  margin-top: 12px;
  padding: 10px;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
}

.warning-item {
  color: #ffc107;
  font-size: 11px;
  margin: 0;
  padding: 2px 0;
  text-align: left;

  &:last-child {
    padding-bottom: 0;
  }
}

.confirm-btn {
  width: 100%;
  background: linear-gradient(135deg, #28a745, #20c997);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  padding: 12px;
  margin-top: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// History Section
.history-section {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px;
}

.section-header {
  margin-bottom: 8px;
}

.section-title {
  color: #ffcc00;
  font-size: 13px;
  font-weight: 500;
  margin: 0;
}

.history-list {
  min-height: 60px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
}

.empty-icon {
  font-size: 24px;
  margin-bottom: 4px;
  opacity: 0.5;
}

.empty-text {
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  margin: 0;
}

.history-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.history-index {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(212, 175, 55, 0.2);
  border-radius: 50%;
  font-size: 10px;
  font-weight: 600;
  color: #d4af37;
  flex-shrink: 0;
}

.history-info {
  flex: 1;
  min-width: 0;
}

.history-prize {
  display: flex;
  align-items: center;
  gap: 4px;

  .prize-icon {
    font-size: 12px;
  }

  .prize-value {
    font-size: 12px;
    font-weight: 700;
    color: #ffd700;
  }

  .prize-arrow {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.4);
  }

  .prize-credit {
    font-size: 12px;
    font-weight: 700;
    color: #28a745;
  }
}

.history-date {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
}

// Modal Overlay
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 9999;
  animation: fadeIn 0.15s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Info Modal
.info-modal {
  background: linear-gradient(
    135deg,
    rgba(16, 16, 30, 0.95),
    rgba(26, 26, 46, 0.95)
  );
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 204, 0, 0.2);
  max-width: 360px;
  width: 100%;
  animation: slideUp 0.2s ease-out;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-title {
  color: #ffcc00;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.modal-close {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: #fff;
  width: 26px;
  height: 26px;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
}

.modal-body {
  padding: 14px 16px;
}

.terms-list {
  margin: 0;
  padding-left: 18px;
  list-style-type: none;

  li {
    position: relative;
    color: rgba(255, 255, 255, 0.85);
    font-size: 12px;
    line-height: 1.6;
    margin-bottom: 6px;
    padding-left: 4px;

    &::before {
      content: '•';
      position: absolute;
      left: -14px;
      color: #ffcc00;
    }

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: #ffcc00;
      font-weight: 600;
    }
  }
}

.modal-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 16px 14px 16px;
}
</style>
